import { useAuthStore } from '../store/authStore';
import { signRequestWithKey } from '../crypto/signing';
import type { SigningKey } from '../crypto/ed25519Provider';
import type {
  WebSocketMessage,
  CallSessionId,
  DirectFileTransferSessionId,
  DeviceLocalCircleDataDeletionAuthorization,
  WSDirectFileTransferMetadata
} from '@shared/types';
import { normalizeServerUrl } from '../utils/serverKey';
import { markCircleServerAccessRemoved } from '../utils/removedCircleAccess';

type MessageHandler = (message: WebSocketMessage) => void;
type AuthMode = 'device' | 'external' | 'temporary';
type WsContextRole = 'app' | 'call';
type WsContextSource = AuthMode | 'call';
type ConnectionIssueCode = 'server_not_responding' | 'auth_failed';
type ExternalRegistrationPayload = {
  externalIdentityId: string;
  externalPublicKey: {
    algorithm: 'ed25519' | 'x25519';
    value: string;
  };
  displayName?: string;
  admission:
    | {
        kind: 'call_link';
        callLinkId: string;
        callLinkSecret: string;
        targetIdentityId: string;
      }
    | {
        kind: 'whitelist_key';
        targetIdentityId: string;
      };
};

type ServerConnectionSnapshot = {
  state: 'connected' | 'disconnected' | 'never';
  issueCode: ConnectionIssueCode | null;
  lastConnectedAt: number | null;
  lastDisconnectedAt: number | null;
};

type ServerConnectionState = {
  lastConnectedAt: number | null;
  lastDisconnectedAt: number | null;
  issueCode: ConnectionIssueCode | null;
};

export type WsBoundContext = {
  role: WsContextRole;
  source: WsContextSource;
  deviceId?: string;
  identityId?: string;
  serverUrl: string;
  serverOrigin: string;
};

type CallLockState = {
  callSessionId: string;
  context: WsBoundContext;
  phase: 'signaling' | 'active-call';
  acquiredAt: number;
};

const HEARTBEAT_INTERVAL_MS = (() => {
  const raw = Number((import.meta as any).env?.VITE_WS_HEARTBEAT_MS || 30000);
  if (!Number.isFinite(raw) || raw <= 0) return 30000;
  return Math.max(15000, Math.min(120000, Math.floor(raw)));
})();

const OUTBOUND_NON_PING_WINDOW_MS = 60_000;
const OUTBOUND_NON_PING_SOFT_LIMIT = 45;
const RATE_LIMITED_COOLDOWN_MS = 15_000;
const CALL_NOT_FOUND_ICE_COOLDOWN_MS = 10_000;
const PENDING_QUEUE_LIMIT = 200;

function normalizeExplicitWsUrl(input: string): string {
  try {
    const url = new URL(input);
    const isLocalhost =
      url.hostname === 'localhost'
      || url.hostname === '127.0.0.1'
      || url.hostname === '::1'
      || url.hostname === '[::1]';

    if (!import.meta.env.DEV && !isLocalhost && url.protocol === 'ws:') {
      url.protocol = 'wss:';
    }
    return url.toString();
  } catch {
    return input;
  }
}

class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;

  private isAuthenticated = false;
  private pendingAppMessages: WebSocketMessage[] = [];
  private pendingCallMessages: WebSocketMessage[] = [];
  private connectedServerOrigin: string | null = null;
  private pendingServerOrigin: string | null = null;
  private connectedContext: WsBoundContext | null = null;
  private pendingContext: WsBoundContext | null = null;
  private appContext: WsBoundContext | null = null;
  private callLock: CallLockState | null = null;
  private isIntentionalDisconnect = false;
  private wasAuthenticatedOnCurrentSocket = false;
  private serverConnectionState = new Map<string, ServerConnectionState>();
  private authMode: AuthMode = 'device';
  private externalWsBaseUrl: string | null = null;
  private externalRegistrationRequest: {
    signerId: string;
    signingKey: SigningKey;
    payload: ExternalRegistrationPayload;
  } | null = null;
  private temporaryRegistrationRequest: {
    serverUrl: string;
    deviceId: string;
    signingKey: SigningKey;
  } | null = null;
  private outboundWindowStart = 0;
  private outboundNonPingCount = 0;
  private droppedIceCandidatesInWindow = 0;
  private iceSendSuppressedUntil = 0;

  constructor() {
    this.refreshAppContextFromStore();

    useAuthStore.subscribe((state, prevState) => {
      const previousAppContext = this.deriveAppContextFromState(prevState);
      const nextAppContext = this.deriveAppContextFromState(state);
      this.appContext = nextAppContext;

      if (this.callLock) {
        return;
      }

      const hadPrevious = !!previousAppContext;
      const hasNext = !!nextAppContext;

      if (!hadPrevious && hasNext) {
        this.connect();
      } else if (hadPrevious && !hasNext) {
        this.disconnect();
      } else if (
        hadPrevious
        && hasNext
        && !this.areContextsEquivalent(previousAppContext, nextAppContext)
      ) {
        this.disconnect({ clearPending: false });
        this.connect();
      }
    });
  }

  private isDeviceScopedMessageType(type: string): boolean {
    return type.startsWith('message:') || type.startsWith('system:');
  }

  private classifyMessageRole(message: WebSocketMessage): WsContextRole {
    return message.type.startsWith('call:') ? 'call' : 'app';
  }

  private getPendingQueue(role: WsContextRole): WebSocketMessage[] {
    return role === 'call' ? this.pendingCallMessages : this.pendingAppMessages;
  }

  private queuePendingMessage(message: WebSocketMessage): void {
    const queue = this.getPendingQueue(this.classifyMessageRole(message));
    if (queue.length >= PENDING_QUEUE_LIMIT) {
      queue.shift();
    }
    queue.push(message);
  }

  private drainPendingMessages(): WebSocketMessage[] {
    const combined = [...this.pendingCallMessages, ...this.pendingAppMessages];
    combined.sort((a, b) => {
      const left = typeof a.timestamp === 'number' ? a.timestamp : 0;
      const right = typeof b.timestamp === 'number' ? b.timestamp : 0;
      return left - right;
    });
    this.pendingCallMessages = [];
    this.pendingAppMessages = [];
    return combined;
  }

  private isExpectedBackgroundSyncError(error: unknown): boolean {
    const message = error instanceof Error ? (error.message || '').trim() : '';
    return message === 'WS_NOT_READY_FOR_SERVER' || message === 'SERVER_CONTEXT_SWITCH_TIMEOUT';
  }

  private resetOutboundWindowIfNeeded(now: number): void {
    if (this.outboundWindowStart === 0 || now - this.outboundWindowStart >= OUTBOUND_NON_PING_WINDOW_MS) {
      this.outboundWindowStart = now;
      this.outboundNonPingCount = 0;
      this.droppedIceCandidatesInWindow = 0;
    }
  }

  private shouldDropMessageForClientRateLimit(messageType: string): boolean {
    if (messageType === 'call:ice-candidate') {
      return false;
    }

    const now = Date.now();
    this.resetOutboundWindowIfNeeded(now);

    if (this.iceSendSuppressedUntil > now) {
      return true;
    }

    if (this.outboundNonPingCount < OUTBOUND_NON_PING_SOFT_LIMIT) {
      return false;
    }

    this.droppedIceCandidatesInWindow += 1;
    return true;
  }

  private wsUrlToHttpOrigin(wsUrl: string): string | null {
    try {
      const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost';
      const url = new URL(wsUrl, base);
      url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
      url.pathname = '';
      url.search = '';
      url.hash = '';
      return normalizeServerUrl(url.origin) || null;
    } catch {
      return null;
    }
  }

  private buildContext(params: {
    role: WsContextRole;
    source: WsContextSource;
    serverUrl: string;
    deviceId?: string;
    identityId?: string;
  }): WsBoundContext | null {
    const normalizedServerUrl = normalizeServerUrl(params.serverUrl);
    if (!normalizedServerUrl) return null;
    const serverOrigin = this.wsUrlToHttpOrigin(normalizedServerUrl);
    if (!serverOrigin) return null;
    return {
      role: params.role,
      source: params.source,
      deviceId: params.deviceId,
      identityId: params.identityId,
      serverUrl: normalizedServerUrl,
      serverOrigin
    };
  }

  private areContextsEquivalent(a: WsBoundContext | null, b: WsBoundContext | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.role === b.role
      && a.source === b.source
      && (a.deviceId || '') === (b.deviceId || '')
      && (a.identityId || '') === (b.identityId || '')
      && a.serverOrigin === b.serverOrigin
      && a.serverUrl === b.serverUrl;
  }

  private deriveAppContextFromState(state: ReturnType<typeof useAuthStore.getState>): WsBoundContext | null {
    const explicit = import.meta.env.VITE_WS_URL as string | undefined;

    if (this.authMode === 'external') {
      return this.externalWsBaseUrl
        ? this.buildContext({
            role: 'app',
            source: 'external',
            serverUrl: this.externalWsBaseUrl
          })
        : null;
    }

    if (this.authMode === 'temporary') {
      return this.temporaryRegistrationRequest
        ? this.buildContext({
            role: 'app',
            source: 'temporary',
            serverUrl: this.temporaryRegistrationRequest.serverUrl,
            deviceId: this.temporaryRegistrationRequest.deviceId
          })
        : null;
    }

    const currentDeviceId = state.currentDeviceId;
    const device = currentDeviceId ? state.devices[currentDeviceId] : undefined;
    const identity = device ? state.identities[device.identityId] : undefined;
    const serverUrl = explicit ? normalizeExplicitWsUrl(explicit) : identity?.serverUrl;
    if (!serverUrl) return null;

    return this.buildContext({
      role: 'app',
      source: 'device',
      serverUrl,
      deviceId: device?.deviceId,
      identityId: identity?.identityId
    });
  }

  refreshAppContextFromStore(): void {
    this.appContext = this.deriveAppContextFromState(useAuthStore.getState());
  }

  private getEffectiveContext(): WsBoundContext | null {
    return this.callLock?.context || this.appContext;
  }

  private getEffectiveDeviceId(): string | null {
    const deviceId = this.getEffectiveContext()?.deviceId;
    return deviceId ? String(deviceId) : null;
  }

  private isMessageCompatibleWithEffectiveContext(message: WebSocketMessage): boolean {
    const currentDeviceId = this.getEffectiveDeviceId();
    const messageDeviceId = (message as { data?: { deviceId?: unknown } })?.data?.deviceId;

    if (messageDeviceId === undefined || messageDeviceId === null) {
      return true;
    }

    if (!currentDeviceId) return false;
    return String(messageDeviceId) === currentDeviceId;
  }

  private resolveWsUrl(): string | null {
    const context = this.getEffectiveContext();
    if (!context) return null;

    const explicit = import.meta.env.VITE_WS_URL as string | undefined;
    if (explicit && context.source === 'device' && context.role === 'app') {
      return normalizeExplicitWsUrl(explicit);
    }

    const rawPath = (import.meta.env.VITE_WS_PATH as string | undefined) || '/ws';
    const wsPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

    try {
      const url = new URL(context.serverUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = wsPath;
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return null;
    }
  }

  acquireCallLock(params: {
    callSessionId: string;
    deviceId: string;
    identityId?: string;
    serverUrl: string;
  }): void {
    const context = this.buildContext({
      role: 'call',
      source: 'call',
      deviceId: params.deviceId,
      identityId: params.identityId,
      serverUrl: params.serverUrl
    });
    if (!context) {
      throw new Error('INVALID_CALL_WS_CONTEXT');
    }

    const previousEffective = this.getEffectiveContext();
    this.callLock = {
      callSessionId: params.callSessionId,
      context,
      phase: 'signaling',
      acquiredAt: Date.now()
    };
    const nextEffective = this.getEffectiveContext();

    if (!this.areContextsEquivalent(previousEffective, nextEffective)) {
      this.disconnect({ clearPending: false });
      this.connect();
    }
  }

  promoteCallLock(callSessionId: string, phase: 'active-call'): void {
    if (this.callLock?.callSessionId !== callSessionId) return;
    this.callLock = {
      ...this.callLock,
      phase
    };
  }

  releaseCallLock(callSessionId: string): void {
    if (this.callLock?.callSessionId !== callSessionId) return;

    const previousEffective = this.getEffectiveContext();
    this.callLock = null;
    const nextEffective = this.getEffectiveContext();

    if (!this.areContextsEquivalent(previousEffective, nextEffective)) {
      this.disconnect({ clearPending: false });
      this.connect();
    }
  }

  getConnectionContext(): WsBoundContext | null {
    return this.connectedContext || this.pendingContext || this.getEffectiveContext();
  }

  async connectExternal(params: {
    serverUrl: string;
    signerId: string;
    signingKey: SigningKey;
    payload: ExternalRegistrationPayload;
    timeoutMs?: number;
  }): Promise<void> {
    this.authMode = 'external';
    this.externalWsBaseUrl = params.serverUrl;
    this.externalRegistrationRequest = {
      signerId: params.signerId,
      signingKey: params.signingKey,
      payload: params.payload
    };
    this.refreshAppContextFromStore();

    if (this.isConnectedToServer(params.serverUrl)) {
      return;
    }

    this.disconnect();
    this.connect();

    const timeoutMs = Math.max(2000, params.timeoutMs || 12000);
    await new Promise<void>((resolve, reject) => {
      let done = false;
      const cleanup = () => {
        done = true;
        unsubscribeRegistered();
        unsubscribeError();
        window.clearTimeout(timeout);
      };
      const finishOk = () => {
        if (done) return;
        cleanup();
        resolve();
      };
      const finishErr = (message: string) => {
        if (done) return;
        cleanup();
        reject(new Error(message));
      };

      const unsubscribeRegistered = this.on('registered', () => finishOk());
      const unsubscribeError = this.on('error', (message) => {
        const code = ((message.data as any)?.code || '').toString();
        if (
          code === 'INVALID_SIGNATURE'
          || code === 'INVALID_NONCE'
          || code === 'FORBIDDEN'
          || code === 'INVALID_STATE'
          || code === 'NOT_FOUND'
          || code === 'INVITE_EXPIRED'
        ) {
          finishErr(((message.data as any)?.message || 'External registration failed').toString());
        }
      });
      const timeout = window.setTimeout(() => {
        finishErr('WebSocket external registration timeout');
      }, timeoutMs);
    });
  }

  async connectTemporary(params: {
    serverUrl: string;
    deviceId: string;
    signingKey: SigningKey;
    timeoutMs?: number;
  }): Promise<void> {
    this.authMode = 'temporary';
    this.temporaryRegistrationRequest = {
      serverUrl: params.serverUrl,
      deviceId: params.deviceId,
      signingKey: params.signingKey
    };
    this.externalWsBaseUrl = null;
    this.externalRegistrationRequest = null;
    this.refreshAppContextFromStore();

    if (this.isConnectedToServer(params.serverUrl)) return;

    this.disconnect();
    this.connect();

    const timeoutMs = Math.max(2000, params.timeoutMs || 12000);
    await new Promise<void>((resolve, reject) => {
      let done = false;
      const cleanup = () => {
        done = true;
        unsubscribeRegistered();
        unsubscribeError();
        window.clearTimeout(timeout);
      };
      const finishOk = () => {
        if (done) return;
        cleanup();
        resolve();
      };
      const finishErr = (message: string) => {
        if (done) return;
        cleanup();
        reject(new Error(message));
      };

      const unsubscribeRegistered = this.on('registered', () => finishOk());
      const unsubscribeError = this.on('error', (message) => {
        const code = ((message.data as any)?.code || '').toString();
        if (code) {
          finishErr(((message.data as any)?.message || code).toString());
        }
      });
      const timeout = window.setTimeout(() => finishErr('WebSocket temporary registration timeout'), timeoutMs);
    });
  }

  leaveExternalMode() {
    this.disconnect();
    this.authMode = 'device';
    this.externalWsBaseUrl = null;
    this.externalRegistrationRequest = null;
    this.temporaryRegistrationRequest = null;
    this.refreshAppContextFromStore();
  }

  isExternalMode(): boolean {
    return this.authMode === 'external';
  }

  leaveTemporaryMode() {
    this.disconnect();
    this.authMode = 'device';
    this.temporaryRegistrationRequest = null;
    this.refreshAppContextFromStore();
  }

  isTemporaryMode(): boolean {
    return this.authMode === 'temporary';
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const wsUrl = this.resolveWsUrl();
    const effectiveContext = this.getEffectiveContext();
    if (!wsUrl || !effectiveContext) {
      console.error('WebSocket not started: missing effective WS context');
      return;
    }

    this.isIntentionalDisconnect = false;
    this.pendingServerOrigin = effectiveContext.serverOrigin;
    this.pendingContext = effectiveContext;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.wasAuthenticatedOnCurrentSocket = false;
        this.connectedServerOrigin = this.wsUrlToHttpOrigin(this.ws?.url || wsUrl);
        this.connectedContext = this.pendingContext;
        if (this.connectedServerOrigin) {
          this.markServerConnected(this.connectedServerOrigin);
        }
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        void this.authenticate();
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = () => {
        const origin = this.connectedServerOrigin || this.pendingServerOrigin;
        if (origin && !this.isIntentionalDisconnect) {
          this.markServerIssue(origin, 'server_not_responding');
        }
      };

      this.ws.onclose = () => {
        const origin = this.connectedServerOrigin || this.pendingServerOrigin;
        const intentional = this.isIntentionalDisconnect;
        const wasAuthenticated = this.wasAuthenticatedOnCurrentSocket;
        this.isAuthenticated = false;
        this.connectedServerOrigin = null;
        this.pendingServerOrigin = null;
        this.connectedContext = null;
        this.pendingContext = null;
        this.wasAuthenticatedOnCurrentSocket = false;
        this.isIntentionalDisconnect = false;
        this.stopHeartbeat();
        if (origin) {
          this.markServerDisconnected(origin);
          if (!intentional && !wasAuthenticated) {
            this.markServerIssue(origin, 'server_not_responding');
          }
        }
        if (!intentional) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  disconnect(options?: { clearPending?: boolean }) {
    const clearPending = options?.clearPending !== false;

    this.isIntentionalDisconnect = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isAuthenticated = false;
    this.connectedServerOrigin = null;
    this.pendingServerOrigin = null;
    this.connectedContext = null;
    this.pendingContext = null;
    this.wasAuthenticatedOnCurrentSocket = false;

    if (clearPending) {
      this.pendingAppMessages = [];
      this.pendingCallMessages = [];
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    if (this.reconnectTimeout) {
      return;
    }

    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private getServerState(origin: string): ServerConnectionState {
    const existing = this.serverConnectionState.get(origin);
    if (existing) return existing;
    const created: ServerConnectionState = {
      lastConnectedAt: null,
      lastDisconnectedAt: null,
      issueCode: null
    };
    this.serverConnectionState.set(origin, created);
    return created;
  }

  private markServerConnected(origin: string) {
    const state = this.getServerState(origin);
    state.lastConnectedAt = Date.now();
    state.issueCode = null;
  }

  private markServerDisconnected(origin: string) {
    const state = this.getServerState(origin);
    state.lastDisconnectedAt = Date.now();
  }

  private markServerIssue(origin: string, issueCode: ConnectionIssueCode) {
    const state = this.getServerState(origin);
    state.issueCode = issueCode;
  }

  private startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({
          type: 'ping',
          data: {},
          timestamp: Date.now()
        });
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private async authenticate() {
    if (this.authMode === 'external') {
      if (!this.externalRegistrationRequest) {
        console.error('Missing external registration request');
        return;
      }
      const signedRequest = await signRequestWithKey(
        'ws:register-external',
        this.externalRegistrationRequest.payload,
        this.externalRegistrationRequest.signerId,
        this.externalRegistrationRequest.signingKey
      );
      this.send({
        type: 'register-external',
        data: signedRequest,
        timestamp: Date.now()
      });
      return;
    }

    if (this.authMode === 'temporary') {
      if (!this.temporaryRegistrationRequest) {
        console.error('Missing temporary registration request');
        return;
      }
      const signedRequest = await signRequestWithKey(
        'ws:register',
        {},
        this.temporaryRegistrationRequest.deviceId,
        this.temporaryRegistrationRequest.signingKey
      );
      this.send({
        type: 'register',
        data: signedRequest,
        timestamp: Date.now()
      });
      return;
    }

    const effectiveDeviceId = this.getEffectiveDeviceId();
    const currentDevice = effectiveDeviceId
      ? useAuthStore.getState().devices[effectiveDeviceId]
      : null;

    if (!currentDevice) {
      console.error('No effective device for authentication');
      return;
    }

    const signedRequest = await signRequestWithKey(
      'ws:register',
      {},
      currentDevice.deviceId,
      currentDevice.signingKey
    );

    this.send({
      type: 'register',
      data: signedRequest,
      timestamp: Date.now()
    });
  }

  private handleMessage(message: WebSocketMessage) {
    if (message.type === 'registered') {
      this.isAuthenticated = true;
      this.wasAuthenticatedOnCurrentSocket = true;

      const queuedMessages = this.drainPendingMessages();
      const pendingToSend = queuedMessages.filter((msg) => this.isMessageCompatibleWithEffectiveContext(msg));
      pendingToSend.forEach((msg) => this.send(msg));

      this.notifyHandlers('registered', message);
      return;
    }

    if (message.type === 'error') {
      const code = (message.data as any)?.code as string | undefined;
      if (code === 'RATE_LIMITED') {
        this.iceSendSuppressedUntil = Date.now() + RATE_LIMITED_COOLDOWN_MS;
      } else if (code === 'CALL_NOT_FOUND') {
        this.iceSendSuppressedUntil = Date.now() + CALL_NOT_FOUND_ICE_COOLDOWN_MS;
      } else {
        console.error('WebSocket error message:', message.data);
      }

      const isAuthFailure = !!code && [
        'DEVICE_NOT_FOUND',
        'DEVICE_REVOKED',
        'INVALID_SIGNATURE',
        'INVALID_DEVICE',
        'IDENTITY_NOT_FOUND',
        'IDENTITY_SUSPENDED',
        'FORBIDDEN',
        'INVALID_NONCE'
      ].includes(code);

      if (isAuthFailure) {
        if ((code === 'DEVICE_NOT_FOUND' || code === 'IDENTITY_NOT_FOUND') && this.connectedContext?.identityId) {
          if (markCircleServerAccessRemoved(this.connectedContext.identityId)) {
            window.dispatchEvent(new CustomEvent('circlus:server-access-removed', {
              detail: { identityId: this.connectedContext.identityId }
            }));
          }
        }
        const origin = this.connectedServerOrigin || this.pendingServerOrigin;
        if (origin) {
          this.markServerIssue(origin, 'auth_failed');
        }
        this.disconnect();
      }

      this.notifyHandlers('error', message);
      return;
    }

    if (message.type === 'identity:server-data-deleted') {
      const identityId = String((message.data as { identityId?: string })?.identityId || this.connectedContext?.identityId || '').trim();
      if (identityId && markCircleServerAccessRemoved(identityId)) {
        window.dispatchEvent(new CustomEvent('circlus:server-access-removed', {
          detail: { identityId }
        }));
      }
      this.notifyHandlers(message.type, message);
      this.disconnect();
      return;
    }

    if (message.type === 'device:delete-local-circle-data') {
      const data = message.data as {
        identityId?: string;
        localDeletionAuthorization?: DeviceLocalCircleDataDeletionAuthorization;
      };
      const identityId = String(data?.identityId || this.connectedContext?.identityId || '').trim();
      if (identityId && data.localDeletionAuthorization) {
        window.dispatchEvent(new CustomEvent('circlus:delete-revoked-device-local-data', {
          detail: {
            identityId,
            localDeletionAuthorization: data.localDeletionAuthorization
          }
        }));
      }
      this.notifyHandlers(message.type, message);
      this.disconnect();
      return;
    }

    this.notifyHandlers(message.type, message);
  }

  private notifyHandlers(type: string, message: WebSocketMessage) {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          const result = (handler as (msg: WebSocketMessage) => unknown)(message);
          if (result && typeof (result as { then?: unknown }).then === 'function') {
            (result as Promise<unknown>).catch((error) => {
              if (this.isExpectedBackgroundSyncError(error)) {
                return;
              }
              console.error('Message handler async error:', error);
            });
          }
        } catch (error) {
          console.error('Message handler error:', error);
        }
      });
    }

    const wildcardHandlers = this.messageHandlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          const result = (handler as (msg: WebSocketMessage) => unknown)(message);
          if (result && typeof (result as { then?: unknown }).then === 'function') {
            (result as Promise<unknown>).catch((error) => {
              if (this.isExpectedBackgroundSyncError(error)) {
                return;
              }
              console.error('Wildcard handler async error:', error);
            });
          }
        } catch (error) {
          console.error('Wildcard handler error:', error);
        }
      });
    }
  }

  on(type: string, handler: MessageHandler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }

    this.messageHandlers.get(type)!.add(handler);

    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(type);
        }
      }
    };
  }

  send(message: WebSocketMessage) {
    if (this.authMode === 'external' && this.isDeviceScopedMessageType(message.type)) {
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (message.type === 'ping') return;

      if (message.type !== 'register' && message.type !== 'register-external') {
        this.queuePendingMessage(message);
      }

      this.connect();
      return;
    }

    if (!this.isAuthenticated && message.type !== 'register' && message.type !== 'register-external') {
      if (message.type === 'ping') return;
      this.queuePendingMessage(message);
      return;
    }

    if (this.shouldDropMessageForClientRateLimit(message.type)) {
      return;
    }

    if (message.type !== 'ping' && message.type !== 'call:ice-candidate') {
      const now = Date.now();
      this.resetOutboundWindowIfNeeded(now);
      this.outboundNonPingCount += 1;
    }

    this.ws.send(JSON.stringify(message));
  }

  sendIfReady(message: WebSocketMessage): boolean {
    if (this.authMode === 'external' && this.isDeviceScopedMessageType(message.type)) {
      return false;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isAuthenticated) {
      return false;
    }

    if (this.shouldDropMessageForClientRateLimit(message.type)) {
      return false;
    }

    if (message.type !== 'ping' && message.type !== 'call:ice-candidate') {
      const now = Date.now();
      this.resetOutboundWindowIfNeeded(now);
      this.outboundNonPingCount += 1;
    }

    this.ws.send(JSON.stringify(message));
    return true;
  }

  initiateCall(
    targetIdentityId: string,
    callSessionId: CallSessionId,
    offer: RTCSessionDescriptionInit,
    dtlsSignature?: string
  ) {
    this.send({
      type: 'call:offer',
      data: {
        callSessionId,
        targetIdentityId,
        offer: {
          type: offer.type,
          sdp: offer.sdp,
          dtlsSignature
        }
      },
      timestamp: Date.now()
    });
  }

  answerCall(
    callSessionId: CallSessionId,
    answer: RTCSessionDescriptionInit,
    dtlsSignature?: string
  ) {
    this.send({
      type: 'call:answer',
      data: {
        callSessionId,
        answer: {
          type: answer.type,
          sdp: answer.sdp,
          dtlsSignature
        }
      },
      timestamp: Date.now()
    });
  }

  renegotiateOffer(
    callSessionId: CallSessionId,
    offer: RTCSessionDescriptionInit,
    dtlsSignature?: string
  ) {
    this.send({
      type: 'call:renegotiate-offer',
      data: {
        callSessionId,
        offer: {
          type: offer.type,
          sdp: offer.sdp,
          dtlsSignature
        }
      },
      timestamp: Date.now()
    });
  }

  renegotiateAnswer(
    callSessionId: CallSessionId,
    answer: RTCSessionDescriptionInit,
    dtlsSignature?: string
  ) {
    this.send({
      type: 'call:renegotiate-answer',
      data: {
        callSessionId,
        answer: {
          type: answer.type,
          sdp: answer.sdp,
          dtlsSignature
        }
      },
      timestamp: Date.now()
    });
  }

  sendIceCandidate(callSessionId: CallSessionId, candidate: RTCIceCandidateInit) {
    this.send({
      type: 'call:ice-candidate',
      data: {
        callSessionId,
        candidate
      },
      timestamp: Date.now()
    });
  }

  endCall(callSessionId: CallSessionId) {
    this.send({
      type: 'call:end',
      data: {
        callSessionId
      },
      timestamp: Date.now()
    });
  }

  sendDirectFileTransferOffer(
    sessionId: DirectFileTransferSessionId,
    targetIdentityId: string,
    metadata: WSDirectFileTransferMetadata,
    offer: RTCSessionDescriptionInit
  ) {
    this.send({
      type: 'file-transfer:offer',
      data: {
        sessionId,
        targetIdentityId,
        metadata,
        offer: {
          type: offer.type,
          sdp: offer.sdp
        }
      },
      timestamp: Date.now()
    });
  }

  sendDirectFileTransferAccept(
    sessionId: DirectFileTransferSessionId,
    answer: RTCSessionDescriptionInit
  ) {
    this.send({
      type: 'file-transfer:accept',
      data: {
        sessionId,
        answer: {
          type: answer.type,
          sdp: answer.sdp
        }
      },
      timestamp: Date.now()
    });
  }

  sendDirectFileTransferReject(sessionId: DirectFileTransferSessionId, reason: string) {
    this.send({
      type: 'file-transfer:reject',
      data: { sessionId, reason },
      timestamp: Date.now()
    });
  }

  sendDirectFileTransferIceCandidate(sessionId: DirectFileTransferSessionId, candidate: RTCIceCandidateInit) {
    this.send({
      type: 'file-transfer:ice-candidate',
      data: { sessionId, candidate },
      timestamp: Date.now()
    });
  }

  sendDirectFileTransferCancel(sessionId: DirectFileTransferSessionId, reason: string) {
    this.send({
      type: 'file-transfer:cancel',
      data: { sessionId, reason },
      timestamp: Date.now()
    });
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.isAuthenticated;
  }

  isConnectedToServer(serverUrl?: string): boolean {
    if (!this.isConnected()) return false;
    const targetOrigin = normalizeServerUrl(serverUrl);
    if (!targetOrigin || !this.connectedServerOrigin) return false;
    return targetOrigin === this.connectedServerOrigin;
  }

  getServerConnectionSnapshot(serverUrl?: string): ServerConnectionSnapshot {
    const targetOrigin = normalizeServerUrl(serverUrl);
    if (!targetOrigin) {
      return {
        state: 'never',
        issueCode: null,
        lastConnectedAt: null,
        lastDisconnectedAt: null
      };
    }

    const state = this.serverConnectionState.get(targetOrigin);
    const connected = this.isConnectedToServer(serverUrl);
    if (connected) {
      return {
        state: 'connected',
        issueCode: null,
        lastConnectedAt: state?.lastConnectedAt || null,
        lastDisconnectedAt: state?.lastDisconnectedAt || null
      };
    }

    if (!state) {
      return {
        state: 'never',
        issueCode: null,
        lastConnectedAt: null,
        lastDisconnectedAt: null
      };
    }

    return {
      state: 'disconnected',
      issueCode: state.issueCode,
      lastConnectedAt: state.lastConnectedAt,
      lastDisconnectedAt: state.lastDisconnectedAt
    };
  }
}

export const wsClient = new WebSocketClient();
